import React, { useState } from 'react';
import { FileText, Download, Loader2, AlertCircle, TrendingUp, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import './Reportes.css';

const Reportes = () => {
  const [activeReport, setActiveReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const generarReporteNoProductos = async () => {
    setLoading(true);
    setError(null);
    setReportData(null);
    setActiveReport('no-productos');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reportes/no-productos`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al generar el reporte');
      }

      const data = await response.json();
      setReportData(data);
    } catch (err) {
      console.error('Error generando reporte:', err);
      setError('No se pudo generar el reporte. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const generarReporteAuditoria = async () => {
    setLoading(true);
    setError(null);
    setReportData(null);
    setActiveReport('auditoria');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reportes/auditoria`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al generar el reporte');
      }

      const data = await response.json();
      setReportData(data);
    } catch (err) {
      console.error('Error generando reporte:', err);
      setError('No se pudo generar el reporte. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!reportData || !reportData.usuarios || reportData.usuarios.length === 0) {
      return;
    }

    // Preparar datos para Excel
    const datosExcel = reportData.usuarios.map(usuario => ({
      'Nombre': usuario.nombre,
      'Cédula': usuario.cedula,
      'Estado ORDER': usuario.estado_order,
      'Oficina': usuario.oficina,
      'Email': usuario.email
    }));

    // Crear libro de trabajo
    const ws = XLSX.utils.json_to_sheet(datosExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'NO PRODUCTOS');

    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 30 }, // Nombre
      { wch: 15 }, // Cédula
      { wch: 20 }, // Estado ORDER
      { wch: 20 }, // Oficina
      { wch: 30 }  // Email
    ];
    ws['!cols'] = colWidths;

    // Descargar archivo
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Reporte_NO_PRODUCTOS_${fecha}.xlsx`);
  };

  const exportarListado = (tipo) => {
    if (!reportData || !reportData.listados) return;

    let datos = [];
    let nombreHoja = '';
    
    switch (tipo) {
      case 'revisado_si':
        datos = reportData.listados.revisado_si;
        nombreHoja = 'Revisados SI';
        break;
      case 'revisado_no':
        datos = reportData.listados.revisado_no;
        nombreHoja = 'Revisados NO';
        break;
      case 'cambios_hoy':
        datos = reportData.listados.cambios_hoy;
        nombreHoja = 'Cambios Hoy';
        break;
      case 'con_documentos':
        datos = reportData.listados.con_documentos;
        nombreHoja = 'Con Documentos';
        break;
      case 'sin_documentos':
        datos = reportData.listados.sin_documentos;
        nombreHoja = 'Sin Documentos';
        break;
      default:
        return;
    }

    if (datos.length === 0) {
      alert('No hay datos para exportar en esta categoría');
      return;
    }

    // Preparar datos para Excel
    const datosExcel = datos.map(exp => ({
      'Nombre': exp.nombre,
      'Cédula': exp.cedula,
      'Sede': exp.sede
    }));

    // Crear libro de trabajo
    const ws = XLSX.utils.json_to_sheet(datosExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

    // Ajustar ancho de columnas
    ws['!cols'] = [
      { wch: 30 }, // Nombre
      { wch: 15 }, // Cédula
      { wch: 25 }  // Sede
    ];

    // Descargar archivo
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Auditoria_${nombreHoja.replace(' ', '_')}_${fecha}.xlsx`);
  };

  const limpiarReporte = () => {
    setActiveReport(null);
    setReportData(null);
    setError(null);
  };

  return (
    <div className="reportes-page" data-testid="reportes-page">
      {/* Header */}
      <div className="reportes-header">
        <div className="header-content">
          <div className="header-icon">
            <FileText size={32} />
          </div>
          <div className="header-text">
            <h1 data-testid="reportes-title">Centro de Reportes</h1>
            <p>Genera y exporta reportes personalizados del sistema</p>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="reportes-content">
        {!activeReport ? (
          /* Selector de Reportes */
          <div className="reportes-grid">
            <div className="reporte-card" data-testid="card-no-productos">
              <div className="card-icon">
                <TrendingUp size={32} />
              </div>
              <div className="card-content">
                <h3>NO PRODUCTOS</h3>
                <p>
                  Usuarios con pagos confirmados que no tienen asignado GROUP o PRODUCT en su orden
                </p>
                <div className="card-tags">
                  <span className="tag">Strapi</span>
                  <span className="tag">Usuarios</span>
                </div>
              </div>
              <button
                onClick={generarReporteNoProductos}
                className="card-button"
                data-testid="btn-generar-no-productos"
              >
                <FileText size={18} />
                <span>Generar Reporte</span>
              </button>
            </div>

            <div className="reporte-card" data-testid="card-auditoria">
              <div className="card-icon">
                <FileText size={32} />
              </div>
              <div className="card-content">
                <h3>AUDITORÍA</h3>
                <p>
                  Métricas sobre expedientes revisados, progreso diario y documentos PDF en el Gestor de Expedientes
                </p>
                <div className="card-tags">
                  <span className="tag">Expedientes</span>
                  <span className="tag">Documentos</span>
                </div>
              </div>
              <button
                onClick={generarReporteAuditoria}
                className="card-button"
                data-testid="btn-generar-auditoria"
              >
                <FileText size={18} />
                <span>Generar Reporte</span>
              </button>
            </div>

            {/* Placeholder para futuros reportes */}
            <div className="reporte-card reporte-card-disabled">
              <div className="card-icon disabled">
                <FileText size={32} />
              </div>
              <div className="card-content">
                <h3>Próximamente</h3>
                <p>Más reportes disponibles pronto</p>
                <div className="card-tags">
                  <span className="tag disabled">En desarrollo</span>
                </div>
              </div>
              <button className="card-button" disabled>
                <span>Próximamente</span>
              </button>
            </div>
          </div>
        ) : (
          /* Resultados del Reporte */
          <div className="reporte-results">
            {/* Header de Resultados */}
            <div className="results-header">
              <div className="results-title">
                <FileText size={24} />
                <h2>
                  Reporte: {activeReport === 'no-productos' ? 'NO PRODUCTOS' : 'AUDITORÍA'}
                </h2>
              </div>
              <div className="results-header-actions">
                {activeReport === 'auditoria' && reportData && (
                  <div className="dropdown-container">
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="btn-download-primary"
                      data-testid="btn-download-dropdown"
                    >
                      <Download size={18} />
                      <span>DESCARGAR</span>
                      <ChevronDown size={16} />
                    </button>
                    
                    {dropdownOpen && (
                      <div className="dropdown-menu" data-testid="download-menu">
                        <button
                          onClick={() => { exportarListado('revisado_si'); setDropdownOpen(false); }}
                          className="dropdown-item"
                          data-testid="download-revisado-si"
                        >
                          Expedientes Revisados (SI)
                        </button>
                        <button
                          onClick={() => { exportarListado('revisado_no'); setDropdownOpen(false); }}
                          className="dropdown-item"
                          data-testid="download-revisado-no"
                        >
                          Expedientes Pendientes (NO)
                        </button>
                        <button
                          onClick={() => { exportarListado('cambios_hoy'); setDropdownOpen(false); }}
                          className="dropdown-item"
                          data-testid="download-cambios-hoy"
                        >
                          Cambios Hoy (NO → SI)
                        </button>
                        <button
                          onClick={() => { exportarListado('con_documentos'); setDropdownOpen(false); }}
                          className="dropdown-item"
                          data-testid="download-con-docs"
                        >
                          Con Documentos PDF
                        </button>
                        <button
                          onClick={() => { exportarListado('sin_documentos'); setDropdownOpen(false); }}
                          className="dropdown-item"
                          data-testid="download-sin-docs"
                        >
                          Sin Documentos PDF
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={limpiarReporte}
                  className="btn-back"
                  data-testid="btn-back"
                >
                  Volver a Reportes
                </button>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="results-loading" data-testid="results-loading">
                <Loader2 size={48} className="spinning" />
                <p>Generando reporte...</p>
                <small>Esto puede tomar unos segundos</small>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="results-error" data-testid="results-error">
                <AlertCircle size={24} />
                <span>{error}</span>
              </div>
            )}

            {/* Datos del Reporte */}
            {reportData && !loading && (
              <>
                {/* Renderizar según tipo de reporte */}
                {activeReport === 'auditoria' ? (
                  /* Reporte AUDITORÍA */
                  <>
                    <div className="results-stats" data-testid="results-stats">
                      <div className="stat-card">
                        <span className="stat-label">Total Expedientes</span>
                        <span className="stat-value">{reportData.total_expedientes}</span>
                      </div>
                      <div className="stat-card stat-success">
                        <span className="stat-label">Revisados (SI)</span>
                        <span className="stat-value">{reportData.revisado_si}</span>
                      </div>
                      <div className="stat-card stat-warning">
                        <span className="stat-label">Pendientes (NO)</span>
                        <span className="stat-value">{reportData.revisado_no}</span>
                      </div>
                      <div className="stat-card stat-info">
                        <span className="stat-label">Progreso</span>
                        <span className="stat-value">{reportData.porcentaje_revisado}%</span>
                      </div>
                      <div className="stat-card stat-primary">
                        <span className="stat-label">Cambios Hoy (NO → SI)</span>
                        <span className="stat-value">{reportData.cambios_hoy}</span>
                      </div>
                      <div className="stat-card stat-success">
                        <span className="stat-label">Con Documentos PDF</span>
                        <span className="stat-value">{reportData.documentos.con_documentos}</span>
                      </div>
                      <div className="stat-card stat-error">
                        <span className="stat-label">Sin Documentos PDF</span>
                        <span className="stat-value">{reportData.documentos.sin_documentos}</span>
                      </div>
                    </div>

                    <div className="results-summary" data-testid="results-summary">
                      <FileText size={48} style={{ color: '#fbb040', opacity: 0.6 }} />
                      <h3>Resumen del Reporte</h3>
                      <p>
                        De {reportData.total_expedientes} expedientes, {reportData.revisado_si} han sido revisados 
                        ({reportData.porcentaje_revisado}% completado). Hoy se revisaron {reportData.cambios_hoy} expedientes.
                      </p>
                      <p style={{ marginTop: '12px', fontSize: '14px', color: '#9ca3af' }}>
                        Documentos verificados: {reportData.documentos.con_documentos} con PDF, {reportData.documentos.sin_documentos} sin PDF
                        (de {reportData.documentos.total_verificados} verificados).
                      </p>
                    </div>
                  </>
                ) : (
                  /* Reporte NO PRODUCTOS */
                  <>
                    {/* Estadísticas */}
                    <div className="results-stats" data-testid="results-stats">
                      <div className="stat-card">
                        <span className="stat-label">Total de Usuarios</span>
                        <span className="stat-value">{reportData.total}</span>
                      </div>
                    </div>

                    {/* Tabla o Estado Vacío */}
                    {reportData.usuarios && reportData.usuarios.length > 0 ? (
                      <>
                        <div className="results-actions">
                          <button
                            onClick={exportarExcel}
                            className="btn-export"
                            data-testid="btn-export-excel"
                          >
                            <Download size={18} />
                            <span>Exportar a Excel</span>
                          </button>
                        </div>

                        <div className="results-table-container">
                          <table className="results-table" data-testid="results-table">
                            <thead>
                              <tr>
                                <th>Nombre</th>
                                <th>Cédula</th>
                                <th>Estado ORDER</th>
                                <th>Oficina</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.usuarios.map((usuario, index) => (
                                <tr key={index} data-testid={`usuario-row-${index}`}>
                                  <td>{usuario.nombre}</td>
                                  <td>{usuario.cedula}</td>
                                  <td>
                                    <span className="estado-badge">
                                      Falta: {usuario.estado_order}
                                    </span>
                                  </td>
                                  <td>{usuario.oficina}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="results-empty" data-testid="results-empty">
                        <FileText size={64} />
                        <h3>No se encontraron resultados</h3>
                        <p>No hay usuarios que cumplan los criterios del reporte</p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reportes;
